import { granularityOptions } from './dashboard.constants';
import { Component, inject, OnInit } from '@angular/core';
import { GoogleChartsModule, ChartType } from 'angular-google-charts';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { FilterAreaComponent } from "src/app/components/filter-area/filter-area.component";
import { TFilterAction } from 'src/app/interfaces/components/filter.interface';
import { FormGroup } from '@angular/forms';
import { InputComponent } from "src/app/components/input/input.component";
import { TranslateModule } from '@ngx-translate/core';
import { AlertService } from 'src/app/services/alert.service';
import { SelectComponent } from "src/app/components/select/select.component";
import { createBigNumbers, formatCurrency, generateTooltipHtml } from './dashboard-chart.helper';
import { comparisonChartColumns, comparisonChartOptions, salesEvolutionOptions, salesEvolutionColumns } from './dashboard-chart.config';
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    GoogleChartsModule,
    FilterAreaComponent,
    InputComponent,
    TranslateModule,
    SelectComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private readonly dashboardService = inject(DashboardService);
  private readonly alertService = inject(AlertService);

  form: FormGroup = this.dashboardService.formFilterList;
  granularityOptions = granularityOptions;

  bigNumbers: any[] = [];

  comparisonChartData: any[] = [];
  comparisonChartType = ChartType.ColumnChart;
  comparisonChartColumns = comparisonChartColumns;
  comparisonChartOptions = comparisonChartOptions;
  chartWidth = '100%';

  salesEvolutionData: any[] = [];
  salesEvolutionType = ChartType.LineChart;
  salesEvolutionColumns = salesEvolutionColumns;
  salesEvolutionOptions = salesEvolutionOptions;
  salesEvolutionWidth = '100%';

  ngOnInit(): void {
    this.setInitialValueFilter();
    this.getLoadOverview();
    this.getComparison();
    this.getSalesEvolution();
  }

  private setInitialValueFilter(): void {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    this.form.patchValue({
      dateFrom: sevenDaysAgo,
      dateTo: today,
      granularity: 'Day'
    });
  }

  handleFilterAreaEvent(event: TFilterAction) {
    if (event === 'cleanFilters') {
      this.setInitialValueFilter();
    }

    this.getLoadOverview();
    this.getComparison();
    this.getSalesEvolution();
  }

  getLoadOverview(): void {
    this.dashboardService.overview(this.form.getRawValue()).subscribe({
      next: (res: any) => {
        this.bigNumbers = createBigNumbers(
          res.data,
          formatCurrency.bind(this)
        );
      },
      error: (error) => {
        this.alertService.show({
          type: 'error',
          subtitle:
            error?.error?.message ||
            'ALERT_MODAL.DEFAULT_TABLE_MESSAGE_ERROR',
        });
      },
    });
  }

  getComparison(): void {
    this.dashboardService.salesComparison(this.form.getRawValue()).subscribe({
      next: (res: any) => {
        const periods: any[] = res?.data?.periods || [];
        this.buildChartData(periods);
      },
      error: (error) => {
        this.comparisonChartData = [];
        this.alertService.show({
          type: 'error',
          subtitle:
            error?.error?.message ||
            'ALERT_MODAL.DEFAULT_TABLE_MESSAGE_ERROR',
        });
      },
    });
  }

  buildChartData(periods: any[]): void {
    if (!periods.length) {
      this.comparisonChartData = [];
      return;
    }

    this.comparisonChartData = periods.map((item) => [
      item.label,
      item.revenue,
      generateTooltipHtml(item, true),
    ]);

    const calculatedWidth = periods.length * 110;
    this.chartWidth = calculatedWidth > 800 ? `${calculatedWidth}px` : '100%';
  }

  getSalesEvolution(): void {
    const payload = this.form.getRawValue();
    this.dashboardService.salesEvolution(payload).subscribe({
      next: (res: any) => {
        const sales = res?.data ?? [];
        this.buildSalesEvolutionChartData(sales);
      },

      error: (error) => {
        this.comparisonChartData = [];
        this.alertService.show({
          type: 'error',
          subtitle:
            error?.error?.message ||
            'ALERT_MODAL.DEFAULT_TABLE_MESSAGE_ERROR',
        });
      },
    });
  }

  buildSalesEvolutionChartData(periods: any[]): void {
    if (!periods.length) {
      this.salesEvolutionData = [];
      return;
    }

    this.salesEvolutionData = periods.map((item) => {
      const tooltip = generateTooltipHtml(item);

      return [
        item.label,
        item.revenue,
        tooltip,
        item.salesCount,
        tooltip,
      ];
    });

    const calculatedWidth = periods.length * 110;
    this.salesEvolutionWidth = calculatedWidth > 800 ? `${calculatedWidth}px` : '100%';
  }
}
